import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, Sparkles, Save, Flag, Download, FileText, Clock, Lock, Unlock, ShieldAlert, Loader2, Heart, Briefcase, CheckCircle2, Circle, Trash2,
} from "lucide-react";
import { AppHeader } from "../../components/AppHeader";
import { StatusBadge } from "../../components/StatusBadge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Checkbox } from "../../components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import { SECTIONS } from "../../lib/assessmentConfig";
import { ONBOARDING_PHASES, ONBOARDING_TOTAL } from "../../lib/onboardingConfig";
import { STATUSES, ADMIN_FLAGS, SCORE_CATEGORIES, decisionBand } from "../../lib/statuses";
import api, { getToken, API } from "../../lib/api";

const TABS = ["PROFILE", "QUALIFICATIONS", "EXPERIENCE", "JUDGMENT", "CASE STUDY",
  "PROGRESS NOTE", "SCORING", "DOCUMENTS", "TRAINING", "ONBOARDING", "ADMIN NOTES", "ACTIVITY HISTORY"];

function sectionFields(id) {
  const s = SECTIONS.find((x) => x.id === id);
  return s ? s.blocks.filter((b) => b.type === "field" || b.type === "ack") : [];
}

function fmtVal(b, answers, docs) {
  const v = answers[b.key];
  if (b.input === "file") { const d = docs.find((x) => x.field === b.key); return d ? d.original_filename : "—"; }
  if (b.type === "ack") return v ? "✓ Acknowledged" : "Not acknowledged";
  if (Array.isArray(v)) return v.length ? v.join(", ") : "—";
  return v || "—";
}

function AnswerList({ ids, answers, docs }) {
  const blocks = ids.flatMap(sectionFields);
  return (
    <dl className="space-y-4">
      {blocks.map((b) => (
        <div key={b.key} className="grid grid-cols-1 gap-1 border-b border-[#EFEDE7] pb-4 last:border-0 sm:grid-cols-[1fr_1.4fr] sm:gap-6">
          <dt className="text-sm text-[#515E58]">{b.label}</dt>
          <dd className="whitespace-pre-wrap text-sm text-[#1F2924]">{fmtVal(b, answers, docs)}</dd>
        </div>
      ))}
    </dl>
  );
}

function Card({ title, children }) {
  return (
    <div className="rounded-xl border border-[#E5E2DC] bg-white p-6">
      {title && <h3 className="mb-4 font-heading text-lg font-semibold text-[#1F2924]">{title}</h3>}
      {children}
    </div>
  );
}

export default function ApplicantDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [a, setA] = useState(null);
  const [scores, setScores] = useState({});
  const [flags, setFlags] = useState([]);
  const [note, setNote] = useState("");
  const [status, setStatus] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const [aiRationale, setAiRationale] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get(`/admin/applicants/${id}`);
      setA(data);
      setScores(data.scores || {});
      setFlags(data.flags || []);
      setStatus(data.status);
    } catch { toast.error("Could not load applicant."); }
  };
  useEffect(() => { load(); }, [id]);

  const total = useMemo(() => SCORE_CATEGORIES.reduce((sum, c) => sum + (Number(scores[c.key]) || 0), 0), [scores]);
  const band = decisionBand(Object.keys(scores).length ? total : a?.total_score);

  if (!a) return <div className="min-h-screen bg-[#FAF9F6]" />;
  const answers = a.answers || {};
  const docs = a.documents || [];

  const saveScores = async () => {
    try { await api.put(`/admin/applicants/${id}/scores`, { scores }); toast.success("Scores saved"); load(); }
    catch { toast.error("Could not save scores"); }
  };
  const saveFlags = async (next) => {
    setFlags(next);
    try { await api.put(`/admin/applicants/${id}/flags`, { flags: next }); }
    catch { toast.error("Could not update flags"); }
  };
  const addNote = async () => {
    if (!note.trim()) return;
    try { await api.post(`/admin/applicants/${id}/notes`, { text: note }); setNote(""); toast.success("Note added"); load(); }
    catch { toast.error("Could not add note"); }
  };
  const changeStatus = async (s) => {
    setStatus(s);
    try {
      await api.put(`/admin/applicants/${id}/status`, { status: s });
      toast.success(s === "Approved to Advance" ? "Approved — training unlocked & email sent" : "Status updated");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not update status"); }
  };
  const reopen = async () => {
    try { await api.post(`/admin/applicants/${id}/reopen`); toast.success("Assessment reopened for applicant"); load(); }
    catch { toast.error("Could not reopen"); }
  };
  const hire = async () => {
    try { await api.post(`/admin/applicants/${id}/hire`); toast.success("Marked as hired — onboarding unlocked & email sent"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not mark hired"); }
  };
  const removeApplicant = async () => {
    if (!window.confirm(`Permanently delete ${answers.legal_name || a.applicant_name || "this applicant"} and their account? This cannot be undone.`)) return;
    try { await api.delete(`/admin/applicants/${id}`); toast.success("Applicant deleted"); navigate("/admin"); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not delete applicant"); }
  };
  const aiScore = async () => {
    setAiBusy(true);
    try {
      const { data } = await api.post(`/admin/applicants/${id}/ai-score`);
      const s = data.suggestion || {};
      setScores({ cat1: s.cat1, cat2: s.cat2, cat3: s.cat3, cat4: s.cat4, cat5: s.cat5, cat6: s.cat6 });
      setAiRationale(s.rationale || "");
      toast.success("AI suggestion generated (recommendation only)");
    } catch (e) { toast.error(e.response?.data?.detail || "AI scoring unavailable"); }
    finally { setAiBusy(false); }
  };
  const downloadDoc = (d) => window.open(`${API}/files/${d.id}?auth=${getToken()}`, "_blank");

  const tone = band?.tone;
  const bandColor = { success: "text-[#2C5545]", primary: "text-[#2C5545]", warning: "text-[#B4791F]", danger: "text-[#C44F4F]" }[tone] || "text-[#515E58]";

  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      <AppHeader />
      <main className="mx-auto max-w-6xl px-4 py-6 sm:px-6">
        <button onClick={() => navigate("/admin")} className="mb-4 inline-flex items-center gap-2 text-sm text-[#515E58] hover:text-[#1F2924]" data-testid="back-to-dashboard">
          <ArrowLeft className="h-4 w-4" /> Back to dashboard
        </button>

        {/* Header */}
        <div className="rounded-xl border border-[#E5E2DC] bg-white p-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="font-heading text-2xl font-bold text-[#1F2924]">{answers.legal_name || a.applicant_name || "Applicant"}</h1>
              <p className="text-sm text-[#515E58]">{a.email}</p>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <StatusBadge status={a.status} />
                {a.culture_alignment && a.culture_alignment !== "NOT COMPLETED" && (
                  <span className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-medium ${a.culture_alignment === "ALIGNED" ? "border-[#3A7D5E]/30 bg-[#EAF3EE] text-[#2C5545]" : a.culture_alignment === "UNSURE" ? "border-[#E89B31]/40 bg-[#FDF6EA] text-[#B4791F]" : "border-[#C44F4F]/30 bg-[#FBEDED] text-[#C44F4F]"}`}>
                    <Heart className="h-3 w-3" /> CULTURE: {a.culture_alignment}
                  </span>
                )}
                {a.qualification_review_required && (
                  <span className="inline-flex items-center gap-1 rounded-full border border-[#E89B31]/40 bg-[#FDF6EA] px-2.5 py-0.5 text-xs font-medium text-[#B4791F]">
                    <ShieldAlert className="h-3 w-3" /> QUALIFICATION REVIEW REQUIRED
                  </span>
                )}
              </div>
            </div>
            <div className="flex flex-col items-stretch gap-2 sm:items-end">
              <label className="text-xs uppercase tracking-wide text-[#515E58]">Change Status</label>
              <Select value={status} onValueChange={changeStatus}>
                <SelectTrigger className="w-full sm:w-72" data-testid="status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
              {a.locked && (
                <Button variant="outline" size="sm" onClick={reopen} data-testid="reopen-btn" className="gap-2 border-[#D56A47] text-[#D56A47] hover:bg-[#FBF2ED]">
                  <Unlock className="h-4 w-4" /> Reopen for edits
                </Button>
              )}
              {!a.hired && (
                <Button size="sm" onClick={hire} data-testid="hire-btn" className="gap-2 bg-[#2C5545] hover:bg-[#224336]">
                  <Briefcase className="h-4 w-4" /> Mark as Hired
                </Button>
              )}
              <Button variant="outline" size="sm" onClick={removeApplicant} data-testid="delete-applicant-btn"
                className="gap-2 border-[#C44F4F]/40 text-[#C44F4F] hover:bg-[#FBEDED]">
                <Trash2 className="h-4 w-4" /> Delete
              </Button>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="PROFILE" className="mt-6">
          <TabsList className="flex h-auto w-full flex-nowrap justify-start gap-1 overflow-x-auto rounded-xl border border-[#E5E2DC] bg-white p-1">
            {TABS.map((t) => (
              <TabsTrigger key={t} value={t} data-testid={`tab-${t.replace(/\s/g, "-").toLowerCase()}`}
                className="whitespace-nowrap rounded-lg px-3 py-2 text-xs font-medium data-[state=active]:bg-[#2C5545] data-[state=active]:text-white">
                {t}
              </TabsTrigger>
            ))}
          </TabsList>

          <div className="mt-4">
            <TabsContent value="PROFILE">
              <Card title="Applicant Profile">
                <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  {[["Legal Name", answers.legal_name], ["Preferred Name", answers.preferred_name],
                    ["Email", a.email], ["Phone", answers.phone], ["City & State", answers.city_state],
                    ["Culture Alignment", a.culture_alignment], ["Culture Completed", a.culture_completed_at],
                    ["Values Version", a.culture_values_version],
                    ["Registered", a.created_at], ["Submitted", a.submitted_at], ["Reviewed", a.reviewed_at],
                    ["Reviewer", a.review_by], ["Approved", a.approved_at], ["Approved By", a.approved_by]].map(([k, v]) => (
                    <div key={k}>
                      <dt className="text-xs uppercase tracking-wide text-[#515E58]">{k}</dt>
                      <dd className="text-sm text-[#1F2924]">
                        {v ? (k.match(/Registered|Submitted|Reviewed|Approved$|Completed$/) ? new Date(v).toLocaleString() : v) : "—"}
                      </dd>
                    </div>
                  ))}
                </dl>
              </Card>
            </TabsContent>

            <TabsContent value="QUALIFICATIONS"><Card title="Education & Qualification"><AnswerList ids={["education"]} answers={answers} docs={docs} /></Card></TabsContent>
            <TabsContent value="EXPERIENCE"><Card title="Professional & Documentation Experience"><AnswerList ids={["experience", "documentation"]} answers={answers} docs={docs} /></Card></TabsContent>
            <TabsContent value="JUDGMENT"><Card title="Professional Judgment"><AnswerList ids={["judgment"]} answers={answers} docs={docs} /></Card></TabsContent>
            <TabsContent value="CASE STUDY"><Card title="Applied Case Study Assignment"><AnswerList ids={["assignment"]} answers={answers} docs={docs} /></Card></TabsContent>

            <TabsContent value="PROGRESS NOTE">
              <Card title="Documentation Challenge">
                {[["Progress Note", answers.progress_note], ["Why billable?", answers.billable_explanation], ["Verify before billing", answers.verify_before_billing]].map(([k, v]) => (
                  <div key={k} className="mb-5 last:mb-0">
                    <div className="text-xs uppercase tracking-wide text-[#515E58]">{k}</div>
                    <div className="mt-1 whitespace-pre-wrap rounded-lg bg-[#FAF9F6] p-4 text-sm text-[#1F2924]">{v || "—"}</div>
                  </div>
                ))}
              </Card>
            </TabsContent>

            <TabsContent value="SCORING">
              <div className="space-y-6">
                <Card title="Competency Scoring (100 points)">
                  <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                    <p className="text-sm text-[#515E58]">Scores are recommendations only. The administrator makes the final decision.</p>
                    <Button variant="outline" onClick={aiScore} disabled={aiBusy} data-testid="ai-score-btn" className="gap-2 border-[#D56A47] text-[#D56A47] hover:bg-[#FBF2ED]">
                      {aiBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} AI Suggest
                    </Button>
                  </div>
                  <div className="space-y-3">
                    {SCORE_CATEGORIES.map((c) => (
                      <div key={c.key} className="flex items-center justify-between gap-4">
                        <label className="text-sm text-[#1F2924]">{c.label} <span className="text-[#515E58]">/ {c.max}</span></label>
                        <Input type="number" min={0} max={c.max} value={scores[c.key] ?? ""} data-testid={`score-${c.key}`}
                          onChange={(e) => setScores((s) => ({ ...s, [c.key]: e.target.value === "" ? "" : Math.min(c.max, Math.max(0, Number(e.target.value))) }))}
                          className="w-24 text-right" />
                      </div>
                    ))}
                  </div>
                  {aiRationale && <p className="mt-4 rounded-lg bg-[#FBF2ED] p-3 text-sm text-[#515E58]"><span className="font-medium text-[#D56A47]">AI rationale: </span>{aiRationale}</p>}
                  <div className="mt-5 flex items-center justify-between rounded-lg bg-[#EAF0ED] p-4">
                    <div>
                      <div className="text-xs uppercase tracking-wide text-[#515E58]">Total Score</div>
                      <div className="font-heading text-3xl font-bold text-[#2C5545]" data-testid="total-score">{total}<span className="text-lg text-[#515E58]">/100</span></div>
                    </div>
                    {band && <div className={`font-heading text-lg font-bold ${bandColor}`} data-testid="decision-band">{band.label}</div>}
                  </div>
                  <div className="mt-4 grid grid-cols-2 gap-2 text-xs text-[#515E58] sm:grid-cols-4">
                    <span>85–100: Strong Candidate</span><span>75–84: Advance / Review</span>
                    <span>65–74: Secondary Review</span><span>Below 65: Do Not Advance</span>
                  </div>
                  <Button onClick={saveScores} data-testid="save-scores-btn" className="mt-5 gap-2 bg-[#2C5545] hover:bg-[#224336]"><Save className="h-4 w-4" /> Save Scores</Button>
                </Card>

                <Card title="Administrator Flags">
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                    {ADMIN_FLAGS.map((f) => {
                      const checked = flags.includes(f);
                      return (
                        <label key={f} className={`flex cursor-pointer items-center gap-2.5 rounded-lg border px-3 py-2.5 text-sm ${checked ? "border-[#E89B31]/50 bg-[#FDF6EA]" : "border-[#E5E2DC] bg-white hover:bg-[#F4F2EC]"}`}>
                          <Checkbox checked={checked} data-testid={`flag-${f}`}
                            onCheckedChange={(v) => saveFlags(v ? [...flags, f] : flags.filter((x) => x !== f))} />
                          <Flag className="h-3.5 w-3.5 text-[#E89B31]" />
                          <span className="text-[#1F2924]">{f}</span>
                        </label>
                      );
                    })}
                  </div>
                </Card>
              </div>
            </TabsContent>

            <TabsContent value="DOCUMENTS">
              <Card title="Uploaded Documents">
                {docs.length === 0 ? <p className="text-sm text-[#515E58]">No documents uploaded.</p> : (
                  <div className="space-y-2">
                    {docs.map((d) => (
                      <div key={d.id} className="flex items-center justify-between rounded-lg border border-[#E5E2DC] p-3">
                        <div className="flex items-center gap-3">
                          <FileText className="h-5 w-5 text-[#2C5545]" />
                          <div>
                            <div className="text-sm font-medium text-[#1F2924]">{d.original_filename}</div>
                            <div className="text-xs text-[#515E58]">{d.field}</div>
                          </div>
                        </div>
                        <Button variant="outline" size="sm" onClick={() => downloadDoc(d)} data-testid={`download-${d.field}`} className="gap-2 border-[#E5E2DC]">
                          <Download className="h-4 w-4" /> View
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            </TabsContent>

            <TabsContent value="TRAINING">
              <Card title="Training Access">
                <div className="flex items-center gap-3">
                  {a.training_unlocked ? <Unlock className="h-6 w-6 text-[#3A7D5E]" /> : <Lock className="h-6 w-6 text-[#515E58]" />}
                  <div>
                    <div className="font-medium text-[#1F2924]">{a.training_unlocked ? "Training Unlocked" : "Training Locked"}</div>
                    <div className="text-sm text-[#515E58]">
                      {a.training_unlocked ? "Set status to 'Approved to Advance' unlocks training automatically." : "Approve the applicant to unlock the training portal."}
                    </div>
                  </div>
                </div>
                <dl className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
                  {[["Approved By", a.approved_by], ["Approved At", a.approved_at], ["Training Started", a.training_started_at], ["Training Completed", a.training_completed_at]].map(([k, v]) => (
                    <div key={k}><dt className="text-xs uppercase tracking-wide text-[#515E58]">{k}</dt>
                      <dd className="text-sm text-[#1F2924]">{v ? (k.includes("At") || k.includes("Started") || k.includes("Completed") ? new Date(v).toLocaleString() : v) : "—"}</dd></div>
                  ))}
                  <div><dt className="text-xs uppercase tracking-wide text-[#515E58]">Lessons Completed</dt>
                    <dd className="text-sm text-[#1F2924]">{(a.training_modules_completed || []).length} of 5</dd></div>
                  <div><dt className="text-xs uppercase tracking-wide text-[#515E58]">Quiz Result</dt>
                    <dd className="text-sm text-[#1F2924]">
                      {a.training_quiz?.score != null
                        ? `${a.training_quiz.score}% — ${a.training_quiz.passed ? "Passed" : "Not passed"} (${a.training_quiz.attempts} attempt${a.training_quiz.attempts === 1 ? "" : "s"})`
                        : "Not attempted"}
                    </dd></div>
                </dl>
              </Card>
            </TabsContent>

            <TabsContent value="ONBOARDING">
              <Card title="New Hire Onboarding">
                {!a.hired ? (
                  <p className="text-sm text-[#515E58]">This applicant has not been hired yet. Use “Mark as Hired” to unlock onboarding.</p>
                ) : (
                  <>
                    <div className="mb-5 flex items-center justify-between rounded-lg bg-[#EAF0ED] p-4">
                      <div className="text-sm text-[#1F2924]">
                        Hired by <span className="font-medium">{a.hired_by}</span>
                        {a.hired_at && <> on {new Date(a.hired_at).toLocaleDateString()}</>}
                      </div>
                      <div className="font-heading text-lg font-bold text-[#2C5545]">
                        {Object.values(a.onboarding?.items || {}).filter((v) => v.completed).length}/{ONBOARDING_TOTAL}
                      </div>
                    </div>
                    <div className="space-y-5">
                      {ONBOARDING_PHASES.map((ph) => (
                        <div key={ph.id}>
                          <h4 className="font-heading font-semibold text-[#1F2924]">{ph.title}</h4>
                          <div className="mt-2 space-y-1.5">
                            {ph.items.map((it) => {
                              const st = a.onboarding?.items?.[it.id];
                              const doc = (a.documents || []).find((d) => d.field === `onboarding_${it.id}`);
                              return (
                                <div key={it.id} className="flex items-center justify-between gap-3 border-b border-[#EFEDE7] py-1.5 last:border-0">
                                  <div className="flex items-center gap-2 text-sm">
                                    {st?.completed ? <CheckCircle2 className="h-4 w-4 shrink-0 text-[#3A7D5E]" /> : <Circle className="h-4 w-4 shrink-0 text-[#C7C2B8]" />}
                                    <span className="text-[#1F2924]">{it.title}</span>
                                  </div>
                                  {doc && (
                                    <Button variant="outline" size="sm" onClick={() => downloadDoc(doc)} className="h-7 gap-1 border-[#E5E2DC] text-xs">
                                      <Download className="h-3 w-3" /> View
                                    </Button>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </Card>
            </TabsContent>

            <TabsContent value="ADMIN NOTES">
              <Card title="Confidential Reviewer Notes">
                <div className="space-y-3">
                  {(a.notes || []).length === 0 && <p className="text-sm text-[#515E58]">No notes yet.</p>}
                  {(a.notes || []).map((n) => (
                    <div key={n.id} className="rounded-lg border border-[#E5E2DC] bg-[#FAF9F6] p-4">
                      <div className="mb-1 flex items-center justify-between text-xs text-[#515E58]">
                        <span className="font-medium text-[#1F2924]">{n.admin_name}</span>
                        <span>{new Date(n.created_at).toLocaleString()}</span>
                      </div>
                      <p className="whitespace-pre-wrap text-sm text-[#1F2924]">{n.text}</p>
                    </div>
                  ))}
                </div>
                <div className="mt-4">
                  <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} placeholder="Add a confidential note…" data-testid="note-input" />
                  <Button onClick={addNote} data-testid="add-note-btn" className="mt-3 bg-[#2C5545] hover:bg-[#224336]">Add Note</Button>
                </div>
              </Card>
            </TabsContent>

            <TabsContent value="ACTIVITY HISTORY">
              <Card title="Audit Trail">
                <div className="space-y-3">
                  {(a.audit || []).slice().reverse().map((e) => (
                    <div key={e.id} className="flex gap-3 border-b border-[#EFEDE7] pb-3 last:border-0">
                      <Clock className="mt-0.5 h-4 w-4 shrink-0 text-[#515E58]" />
                      <div>
                        <div className="text-sm font-medium text-[#1F2924]">{e.event}</div>
                        {e.detail && <div className="text-sm text-[#515E58]">{e.detail}</div>}
                        <div className="text-xs text-[#515E58]">{new Date(e.timestamp).toLocaleString()} · {e.actor}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            </TabsContent>
          </div>
        </Tabs>
      </main>
    </div>
  );
}
